import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/authContext";
import { Users, Trash2, Shield, Plus, Loader2, X, UserCog, CheckCircle2, Clock, Power } from "lucide-react";
import { toast } from "sonner";

const ROLE_META = {
  admin: { label: "Admin",   color: "#CCFF00", desc: "Accès total · Gestion équipe" },
  user:  { label: "User",    color: "#00D4FF", desc: "Dashboard, Routine et KPIs perso" },
};

export default function TeamMode() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [tab, setTab] = useState("active");

  const refresh = async () => {
    try { setMembers(await api.authTeam()); }
    catch (e) {
      if (e?.response?.status === 403) toast.error("Réservé admin");
      else toast.error("Erreur chargement équipe");
    }
  };

  useEffect(() => { refresh(); }, []);

  if (user?.role !== "admin") {
    return (
      <div className="border border-[#1E1E24] rounded-sm p-12 text-center" data-testid="team-no-access">
        <Shield className="w-12 h-12 text-zinc-700 mx-auto mb-4" strokeWidth={1.2} />
        <div className="text-white font-display text-xl mb-1">Accès admin requis</div>
        <div className="text-zinc-500 text-sm">Seuls les admins peuvent gérer l'équipe.</div>
      </div>
    );
  }

  const pending = members.filter(m => !m.is_active);
  const active = members.filter(m => m.is_active);
  const list = tab === "pending" ? pending : active;

  const removeUser = async (uid) => {
    if (!confirm("Supprimer définitivement ce membre ?")) return;
    try { await api.authDeleteUser(uid); toast.success("Membre supprimé"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const updateRole = async (uid, role) => {
    try { await api.authUpdateRole(uid, role); toast.success("Rôle mis à jour"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const activate = async (uid) => {
    try { await api.authActivate(uid); toast.success("Compte validé"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const toggleActive = async (uid) => {
    try { const r = await api.authToggleActive(uid); toast.success(r.is_active ? "Activé" : "Désactivé"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-8 animate-fadeUp" data-testid="team-page">
      <header className="border-l-2 border-[#CCFF00] pl-5 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// TEAM MODE</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Gestion d'équipe</h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">Valide les inscriptions, gère les rôles et désactive les comptes en un clic.</p>
        </div>
        <button data-testid="add-user-btn" onClick={() => setShowAdd(true)}
          className="bg-[#CCFF00] text-black font-medium px-5 py-2.5 rounded-sm hover:bg-[#B3E600] flex items-center gap-2 text-sm glow-volt">
          <Plus className="w-4 h-4" strokeWidth={2} /> Créer un compte
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="Total membres" value={members.length} color="#FFFFFF" />
        <Stat label="Actifs" value={active.length} color="#10B981" />
        <Stat label="En attente validation" value={pending.length} color="#F59E0B" alert={pending.length > 0} />
      </div>

      <div className="border-b border-[#1E1E24] flex gap-0">
        <TabBtn id="active"  label={`Actifs (${active.length})`}    cur={tab} onClick={setTab} />
        <TabBtn id="pending" label={`En attente (${pending.length})`} cur={tab} onClick={setTab} alert={pending.length > 0} />
      </div>

      {list.length === 0 ? (
        <div className="border border-dashed border-[#1E1E24] rounded-sm p-10 text-center text-zinc-500 text-sm">
          {tab === "pending" ? "Aucune validation en attente." : "Aucun membre actif."}
        </div>
      ) : (
        <div className="border border-[#1E1E24] rounded-sm overflow-hidden">
          {list.map((u, i) => (
            <div key={u.id} data-testid={`team-row-${i}`}
              className="grid grid-cols-12 gap-3 items-center px-5 py-4 border-b border-[#1E1E24] last:border-0 hover:bg-white/[0.02] transition">
              <div className="col-span-4">
                <div className="text-white text-sm font-medium flex items-center gap-2">
                  {u.name || "—"}
                  {u.id === user.id && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 bg-[#CCFF00]/15 text-[#CCFF00] rounded-sm">TOI</span>}
                </div>
                <div className="text-[11px] font-mono text-zinc-500 mt-1">{u.email}</div>
              </div>
              <div className="col-span-2">
                <select data-testid={`role-${i}`} value={u.role} onChange={e => updateRole(u.id, e.target.value)}
                  disabled={u.id === user.id}
                  className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-1.5 text-xs text-white focus:border-[#CCFF00]/40 outline-none disabled:opacity-50 w-full">
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </select>
              </div>
              <div className="col-span-2 text-[11px] font-mono text-zinc-500">
                <div>créé : {new Date(u.created_at).toLocaleDateString("fr-FR")}</div>
                {u.last_login && <div className="text-zinc-600 mt-0.5">login : {new Date(u.last_login).toLocaleDateString("fr-FR")}</div>}
              </div>
              <div className="col-span-2 text-center">
                {u.is_active
                  ? <span className="text-[10px] font-mono uppercase px-2 py-0.5 bg-[#10B981]/15 text-[#10B981] rounded-sm">ACTIF</span>
                  : <span className="text-[10px] font-mono uppercase px-2 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B] rounded-sm">EN ATTENTE</span>}
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                {!u.is_active && (
                  <button data-testid={`activate-${i}`} onClick={() => activate(u.id)}
                    className="text-[10px] font-mono uppercase px-3 py-1.5 bg-[#CCFF00] text-black rounded-sm hover:bg-[#B3E600] flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Valider
                  </button>
                )}
                {u.is_active && u.id !== user.id && (
                  <button data-testid={`toggle-${i}`} onClick={() => toggleActive(u.id)} title="Désactiver"
                    className="text-zinc-500 hover:text-orange-400 transition p-1.5">
                    <Power className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                )}
                {u.id !== user.id && (
                  <button data-testid={`remove-${i}`} onClick={() => removeUser(u.id)} title="Supprimer"
                    className="text-zinc-500 hover:text-red-400 transition p-1.5">
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && <AddModal onClose={() => setShowAdd(false)} onSuccess={refresh} />}
    </div>
  );
}

const Stat = ({ label, value, color, alert }) => (
  <div className={`border bg-[#0A0A0C] rounded-sm p-5 ${alert ? "border-[#F59E0B]/40" : "border-[#1E1E24]"}`}>
    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">{label}</div>
    <div className="font-mono text-3xl font-bold" style={{ color }}>{value}</div>
  </div>
);

const TabBtn = ({ id, label, cur, onClick, alert }) => (
  <button data-testid={`tab-${id}`} onClick={() => onClick(id)}
    className={`px-5 py-3 text-xs font-mono uppercase tracking-wider transition border-b-2 flex items-center gap-2 ${
      cur === id ? "text-[#CCFF00] border-[#CCFF00]" : "text-zinc-500 border-transparent hover:text-zinc-300"
    }`}>
    {id === "pending" && <Clock className="w-3 h-3" />}
    {label}
    {alert && cur !== id && <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full" />}
  </button>
);

function AddModal({ onClose, onSuccess }) {
  const [f, setF] = useState({ email: "", password: "", name: "", role: "user" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { toast.error("Mot de passe min. 8 caractères"); return; }
    setLoading(true);
    try {
      await api.authAdminCreate(f);
      toast.success(`${f.name} créé comme ${f.role}`);
      onSuccess(); onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur création");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
        className="bg-[#0A0A0C] border border-[#1E1E24] rounded-sm p-6 w-full max-w-md space-y-4" data-testid="invite-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><UserCog className="w-5 h-5 text-[#CCFF00]" /><span className="font-display text-xl text-white">Créer un compte</span></div>
          <button type="button" onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        <p className="text-xs text-zinc-500">Le compte sera créé déjà actif, l'utilisateur peut se connecter immédiatement.</p>
        {["name", "email", "password"].map(k => (
          <div key={k}>
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              {k === "password" ? "Mot de passe (min. 8)" : k}
            </label>
            <input data-testid={`invite-${k}`} required minLength={k === "password" ? 8 : undefined}
              type={k === "password" ? "password" : (k === "email" ? "email" : "text")}
              value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })}
              className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
          </div>
        ))}
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Rôle</label>
          <select data-testid="invite-role" value={f.role} onChange={e => setF({ ...f, role: e.target.value })}
            className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none">
            <option value="user">User / Model</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <button type="submit" disabled={loading} data-testid="invite-submit"
          className="w-full bg-[#CCFF00] text-black font-medium py-2.5 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {loading ? "Création..." : "Créer"}
        </button>
      </form>
    </div>
  );
}
